/**
 * Seed the Tools rows from a dropped CAM design file, else from the row's own
 * G-code.
 *
 * `operationsForFiles` has already made the rows and named them from the file
 * names. This adds the bit type to open the picker on and a spindle speed. A
 * row no toolpath matches (most posts carry no CAM file) takes the tools its
 * G-code header names instead.
 *
 * A toolpath is claimed by an operation NAME, not by a row: the same cut posted
 * for three controllers is three rows sharing one name, and all three describe
 * the one toolpath. Claiming per row would feed the first controller and starve
 * its two siblings.
 */
import { CamExtractedData, CamToolpath } from '@/workers/camTypes';
import { classifyTool } from '@/workers/camStrings';
import { GcodeTool } from '@/workers/gcodeParser';
import { IBitPublic } from '@/redux/bit/bit.d';
import { IBitType, IUnits, IWood } from '@/redux/post/post.d';
import { V2Bit, V2File, V2Operation } from '../v2Upload.d';
import { bitAnswerKey, ParserAnswers, rpmAnswerKey } from './autofill';
import { emptyBit } from './fileHelpers';
import { BitMatch, matchCatalogBit, matchGenericBit } from './bitMatch';
import { cutKey } from './cutOrder';
import { nameSimilarity } from './textMatch';

const spindleOf = (file: V2File | undefined): number | null =>
  file?.extracted?.spindleSpeed ?? null;

/** How well one toolpath fits a row: mostly name overlap, with a small bonus
 * when the spindle agrees too. */
function score(label: string, spindle: number | null, tp: CamToolpath): number {
  const nameScore = nameSimilarity(label, tp.opName ?? '') * 3;
  // A project cut at one speed throughout would otherwise match every row.
  if (nameScore === 0) return 0;
  return nameScore + (spindle !== null && tp.spindle === spindle ? 1 : 0);
}

/** The best unclaimed toolpath for this row, or null when nothing in the file
 * matches — an unmatched row is simply left as the file names it. */
function matchToolpath(
  label: string,
  claimKey: string,
  spindle: number | null,
  toolpaths: CamToolpath[],
  claimed: Map<string, number>
): CamToolpath | null {
  const mine = claimed.get(claimKey);
  if (mine !== undefined) return toolpaths[mine];

  const taken = new Set(claimed.values());
  const candidates = toolpaths
    .map((tp, i) => ({ i, tp, s: score(label, spindle, tp) }))
    .filter((c) => !taken.has(c.i) && c.s > 0);
  if (candidates.length === 0) return null;

  const best = candidates.reduce((a, b) => (b.s > a.s ? b : a));
  claimed.set(claimKey, best.i);
  return best.tp;
}

const fileOf = (op: V2Operation, files: V2File[]): V2File | undefined =>
  files.find((f) => f.id === op.fileIds[0]);

const claimKeyOf = (op: V2Operation, files: V2File[]): string => {
  const file = fileOf(op, files);
  return file ? cutKey(file) : op.name.trim().toLowerCase();
};

/**
 * For a post whose names match nothing (`tool box BB TOOL 1`): when it is one
 * cut and the design one tool, every toolpath that file could hold names the
 * same bit. Two cuts or two tools would mean pairing by order, which nothing
 * verifies, so those seed nothing.
 */
function soleToolMatch(
  operations: V2Operation[],
  files: V2File[],
  toolpaths: CamToolpath[]
): Map<string, CamToolpath> {
  const out = new Map<string, CamToolpath>();
  const [tool] = toolpaths;
  const sameTool = (tp: CamToolpath) =>
    tp.toolName === tool.toolName &&
    tp.bitType === tool.bitType &&
    tp.spindle === tool.spindle;
  const cuts = new Set(operations.map((op) => claimKeyOf(op, files)));
  if (cuts.size !== 1 || !toolpaths.every(sameTool)) return out;

  operations.forEach((op) => {
    // The only evidence left that this design is this cut's at all
    const spindle = spindleOf(fileOf(op, files));
    if (spindle !== null && spindle === tool.spindle) out.set(op.id, tool);
  });
  return out;
}

const rateNear = (a: number, b: number): boolean =>
  Math.abs(a - b) <= Math.max(0.01, 0.005 * b);

/** What a CAM rate is multiplied by to read in the G-code's units. */
function rateScale(cam: CamExtractedData, units: string | null): number {
  if (!cam.units || !units || cam.units === units) return 1;
  return cam.units === IUnits.MM ? 1 / 25.4 : 25.4;
}

/** The one tool whose toolpaths ran at this file's spindle, feed and plunge.
 * A post copies all three from the toolpath, so together they fingerprint it. */
function fingerprintMatch(
  file: V2File | undefined,
  cam: CamExtractedData
): CamToolpath | null {
  const g = file?.extracted;
  const rates = g?.feedRates ?? [];
  if (!g?.spindleSpeed || rates.length === 0) return null;
  const k = rateScale(cam, g.units);
  const ran = (rate: number | null) =>
    rate === null || rates.some((f) => rateNear(f, rate * k));
  const hits = cam.toolpaths.filter(
    (tp) =>
      tp.spindle === g.spindleSpeed &&
      tp.feed !== null &&
      ran(tp.feed) &&
      ran(tp.plunge)
  );
  const tools = new Set(
    hits.map((tp) => `${tp.toolName}|${tp.toolDia}|${tp.bitType}`)
  );
  return tools.size === 1 ? hits[0] : null;
}

/** Which CAM toolpath each operation's name picks, by operation id. */
function matchByName(
  operations: V2Operation[],
  files: V2File[],
  toolpaths: CamToolpath[]
): Map<string, CamToolpath> {
  const out = new Map<string, CamToolpath>();
  const claimed = new Map<string, number>();
  operations.forEach((op) => {
    const tp = matchToolpath(
      op.name,
      claimKeyOf(op, files),
      spindleOf(fileOf(op, files)),
      toolpaths,
      claimed
    );
    if (tp) out.set(op.id, tp);
  });
  // Any name match means the names are informative, so an unmatched row is
  // evidence its cut is not in this design.
  return out.size > 0 ? out : soleToolMatch(operations, files, toolpaths);
}

const NAME_FRACTIONS: Record<string, number> = {
  '1_16': 0.0625,
  '1_8': 0.125,
  '1_4': 0.25,
  '3_8': 0.375,
  '1_2': 0.5,
  '3_4': 0.75,
};
const NAME_DIA =
  /(?<![\d.])(0?\.(?:0625|125|25|375|5|75)|1_16|1_8|1_4|3_8|1_2|3_4)(?!\d)/g;

/** The one inch bit size a file name gives ("Profile .25", "Pocket 1_8 EM"),
 * else null. */
function nameDia(file: V2File | undefined): number | null {
  if (!file || file.extracted?.units === IUnits.MM) return null;
  const sizes = new Set(
    [...file.name.matchAll(NAME_DIA)].map(
      ([m]) => NAME_FRACTIONS[m] ?? Number(m)
    )
  );
  return sizes.size === 1 ? [...sizes][0] : null;
}

/** Which CAM toolpath each operation is, by operation id: its rates first,
 * then its name, never one whose tool size the file name rules out. */
function matchToolpaths(
  operations: V2Operation[],
  files: V2File[],
  cam: CamExtractedData | null
): Map<string, CamToolpath> {
  if (!cam || cam.toolpaths.length === 0) return new Map();
  const out = matchByName(operations, files, cam.toolpaths);
  operations.forEach((op) => {
    const file = fileOf(op, files);
    const dia = cam.units === IUnits.MM ? null : nameDia(file);
    const fits = (tp: CamToolpath | null | undefined) =>
      dia === null ||
      !tp ||
      tp.toolDia === null ||
      Math.abs(tp.toolDia - dia) < 1e-3;
    const tp = fingerprintMatch(file, {
      ...cam,
      toolpaths: cam.toolpaths.filter(fits),
    });
    if (tp) out.set(op.id, tp);
    else if (!fits(out.get(op.id))) out.delete(op.id);
  });
  return out;
}

/** The seed with a resolved catalog bit filled in as though picked, so the row
 * arrives complete rather than as a filter over a picker still to be opened.
 * Unchanged when the tool matched more than one bit, or none. */
function withCatalogBit(seed: V2Bit, match: BitMatch | null): V2Bit {
  if (!match) return seed;
  return {
    ...seed,
    // As a manual pick would; a type the file itself stated is kept.
    bitType: seed.bitType || (match.bit.bitType ?? ''),
    bitId: match.bit._id,
    brandId: match.brand?._id ?? '',
    bitName: match.bit.name,
    laserOrEndmill: match.bit.laserOrEndmill,
    imageUrl: match.bit.imageUrl ?? '',
  };
}

const DOWNCUT_STOCK = new Set<string>([
  '',
  IWood.WOOD_HARD,
  IWood.WOOD_SOFT,
  IWood.PLYWOOD,
]);

/** The type for an end mill whose name gives no cut direction: Downcut in
 * wood, nothing in other stock. */
const endmillType = (material: string): string =>
  DOWNCUT_STOCK.has(material) ? IBitType.DOWNCUT_ENDMILL : '';

/** A toolpath's bit type and catalog bit. A name with no cut direction takes
 * the material's default type, and the generic bit of it. */
function toolpathTool(
  tp: CamToolpath,
  catalog: IBitPublic[],
  material: string
): { bitType: string; match: BitMatch | null } {
  const named = classifyTool(tp.toolName);
  const bitType =
    tp.bitType ?? (named.bitFamily === 'endmill' ? endmillType(material) : '');
  const geometry = () =>
    bitType
      ? matchGenericBit(
          { bitType, dia: tp.toolDia ?? named.dia, vAngle: tp.vAngle },
          catalog
        )
      : null;
  // The name cannot pick a direction, so the defaulted type must.
  const match =
    bitType && !tp.bitType
      ? geometry()
      : (matchCatalogBit(tp.toolName, catalog) ?? geometry());
  return { bitType, match };
}

/** What a toolpath puts on a row: its speed, and its tool — resolved to a
 * catalog bit where the name allows, else just the type for the picker to open
 * on. Null when it would carry neither. */
function hintFor(
  tp: CamToolpath,
  gcodeSpindle: number | null,
  catalog: IBitPublic[],
  material: string
): V2Bit | null {
  const { bitType, match } = toolpathTool(tp, catalog, material);
  // The g-code is what runs; a speed edited after posting lives only there.
  const rpm = String(gcodeSpindle ?? tp.spindle ?? '');
  if (!bitType && !rpm) return null;
  return withCatalogBit(emptyBit(bitType, rpm), match);
}

const CATALOG_TYPES = new Set<string>(Object.values(IBitType));

/** Each distinct tool a G-code file names, in order. */
function gcodeTools(file: V2File | undefined): GcodeTool[] {
  const seen = new Map<string, GcodeTool>();
  (file?.extracted?.tools ?? []).forEach((t) => {
    const key = `${t.toolNumber}|${t.type}|${t.diameter}|${t.vBitDegree}`;
    if (!seen.has(key)) seen.set(key, t);
  });
  return [...seen.values()];
}

/** The one catalog bit with this tool's geometry. */
function geometryMatch(
  tool: GcodeTool,
  bitType: string,
  file: V2File | undefined,
  catalog: IBitPublic[]
): BitMatch | null {
  // The catalog is matched in inches, so a metric tool is left for the user.
  const unit = tool.unit ?? file?.extracted?.units ?? null;
  return matchGenericBit(
    {
      bitType: bitType || null,
      dia: unit === IUnits.MM ? null : tool.diameter,
      vAngle: tool.vBitDegree,
      bitFamily: tool.type === 'Endmill' ? 'endmill' : undefined,
    },
    catalog
  );
}

/** One G-code tool as a bit: its type and speed, resolved to the one catalog
 * bit with its geometry. Null when it carries neither type nor speed. */
function gcodeBit(
  tool: GcodeTool,
  file: V2File | undefined,
  catalog: IBitPublic[]
): V2Bit | null {
  const bitType = tool.type && CATALOG_TYPES.has(tool.type) ? tool.type : '';
  const rpm = String(tool.spindleSpeed ?? spindleOf(file) ?? '');
  if (!bitType && !rpm) return null;
  return withCatalogBit(
    emptyBit(bitType, rpm),
    geometryMatch(tool, bitType, file, catalog)
  );
}

/** The tools a row's G-code names, else those the same cut's export for
 * another controller names: Buildbotics posts write `T1` and nothing more. */
function namedTools(file: V2File | undefined, files: V2File[]): GcodeTool[] {
  const own = gcodeTools(file);
  if (own.length > 0 || !file) return own;
  const key = cutKey(file);
  const sibling = files.find(
    (f) =>
      f !== file &&
      f.kind === 'gcode' &&
      cutKey(f) === key &&
      gcodeTools(f).length > 0
  );
  return gcodeTools(sibling);
}

/** The bits a row's G-code names, for a row no CAM toolpath matched; just its
 * speed when it names no tool. */
function gcodeHints(
  file: V2File | undefined,
  files: V2File[],
  catalog: IBitPublic[]
): V2Bit[] {
  const bits = namedTools(file, files)
    .map((t) => gcodeBit(t, file, catalog))
    .filter((b): b is V2Bit => b !== null);
  const rpm = spindleOf(file);
  if (bits.length === 0 && rpm !== null) return [emptyBit('', String(rpm))];
  return bits;
}

/** One row with its toolpath's hint on it, or the row itself when there is
 * nothing to add. Returning the same object IS the "unchanged" signal. */
function seedOne(
  op: V2Operation,
  tp: CamToolpath | undefined,
  files: V2File[],
  catalog: IBitPublic[],
  material: string
): V2Operation {
  // Any bit, not just a picked one; re-seeding reverts a typed spindle speed
  if (op.bits.length > 0) return op;

  const file = fileOf(op, files);
  if (!tp) {
    const bits = gcodeHints(file, files, catalog);
    return bits.length > 0 ? { ...op, bits } : op;
  }
  const hint = hintFor(tp, spindleOf(file), catalog, material);
  return hint ? { ...op, bits: [hint] } : op;
}

function seedFrom(
  operations: V2Operation[],
  files: V2File[],
  cam: CamExtractedData | null,
  catalog: IBitPublic[],
  material: string
): V2Operation[] {
  const matched = matchToolpaths(operations, files, cam);
  return operations.map((op) =>
    seedOne(op, matched.get(op.id), files, catalog, material)
  );
}

/** The design files to take tools from, best first: a Carveco .art names the
 * cut direction and part number its Vectric twin's "End Mill" leaves out. */
function toolSources(
  cam: CamExtractedData | null
): [CamExtractedData | null, CamExtractedData?] {
  const other = cam?.companion?.data;
  if (!cam || !other) return [cam];
  return other.format === 'art' ? [other, cam] : [cam, other];
}

/**
 * `operations` with a bit hint filled in from `cam` wherever one matched, and
 * from the row's own G-code elsewhere. A row the first design file names no
 * bit for takes its companion's.
 *
 * A row the user has already picked a bit for is returned untouched — the hint
 * is a starting point, never something that overwrites a real choice. The
 * returned array is the same object when nothing changed, so the caller can
 * skip its dispatch.
 */
export function seedOperationsFromCam(
  operations: V2Operation[],
  files: V2File[],
  cam: CamExtractedData | null,
  catalog: IBitPublic[] = [],
  material = ''
): V2Operation[] {
  const [first, second] = toolSources(cam);
  const seeded = seedFrom(operations, files, first, catalog, material);
  const backup = second
    ? seedFrom(operations, files, second, catalog, material)
    : seeded;
  const named = (op: V2Operation) => Boolean(op.bits[0]?.bitId);
  const next = seeded.map((op, i) =>
    named(op) || !named(backup[i]) ? op : backup[i]
  );
  return next.some((op, i) => op !== operations[i]) ? next : operations;
}

/**
 * What the parsed files alone would put on each row, whatever is on it now.
 *
 * Run against stripped rows rather than the live ones, so the answer is the
 * same on a resumed draft — where every row already carries a bit and the seed
 * itself would decline.
 */
export function operationAnswers(
  operations: V2Operation[],
  files: V2File[],
  cam: CamExtractedData | null,
  catalog: IBitPublic[] = [],
  material = ''
): ParserAnswers {
  const bare = operations.map((op) => ({ ...op, bits: [] }));
  const answers: ParserAnswers = {};
  seedOperationsFromCam(bare, files, cam, catalog, material).forEach((op) => {
    const [bit] = op.bits;
    if (!bit) return;
    // A hint with only a bit TYPE leaves the picker reading "Select bit or
    // laser…", and badging an empty-looking field is worse than not badging it.
    answers[bitAnswerKey(op.id)] = bit.bitId;
    answers[rpmAnswerKey(op.id)] = bit.spindleSpeed;
  });
  return answers;
}
